import { SiteUrls } from '@tableau/shared-urls';
import { IUserNotification } from '@tableau/user-notifications-client-core';
import { isNullOrUndefined } from 'util';

import { Location } from '~commonInfra/utils/Location';
import { ServerFilterClauses } from '~commonInfra/utils/ServerFilterClauses';
import { Set } from '~commonInfra/utils/Set';

import { Messages } from '../../../codegen/Localize';
import { showResumeRunFlowTasksActionDialog } from '../../../flow/actions/ResumeRunFlowTasksAction';
import { RunFlowOnOutputStepsAction } from '../../../flow/actions/RunFlowOnOutputStepsAction';
import { FlowOutputStepInfo } from '../../../flow/models/FlowOutputStepInfo';
import { Flows } from '../../../flow/resources/Flows';
import { RunFlowTasks } from '../../../flow/resources/RunFlowTasks';
import { buildHelpUrl, errorCodesWithHelpContent } from '../../../flow/utils/FlowRunErrorMessage';
import { SliceableHelper } from '../../../httpClient/utils/SliceableHelper';
import { DownloadActions } from '../../../spacesPlatform/services/download/DownloadActions';
import { NotificationMenuItem } from '../IUserNotificationMenuItemProvider';
import { UserNotificationMenuItemDefaultProvider } from '../UserNotificationMenuItemDefaultProvider';

export type FlowNotificationError = {
  errorCode: number;
  errorObjectName: string;
  errorMessageParam: string;
  errorOutputStepName?: string;
}

export type FlowNotificationContent = {
  flowName: string;
  flowId: string;
  failureCount: number; // Total number of failures that triggered Flow Suspension
  numFailingTasks: number; // Total number of Tasks suspended from Flow Suspension
  errors: ReadonlyArray<FlowNotificationError>; // Error codes from a failed Flow
};

export class FlowNotificationMenuItemProvider extends UserNotificationMenuItemDefaultProvider<FlowNotificationContent> {

  public getNotificationMenuItems(notification: IUserNotification<FlowNotificationContent>, siteUrls: SiteUrls): Array<NotificationMenuItem> {
    let defaultItems = super.getNotificationMenuItems(notification);
    const content = notification.content;
    if (content.numFailingTasks > 0) {
      // Go to Scheduled Task
      defaultItems.push({
        title: Messages.scheduledTasksGoToScheduledTasks(),
        testId: 'FlowNotificationScheduledTask-' + notification.id,
        closePanel: true,
        onClick: () => {
          this.markAsRead(notification);
          Location.instance.setUrl(siteUrls.flowScheduledTasksUrl(content.flowId));
        }
      });
      // Resume Schedule
      defaultItems.push({
        title: Messages.flowTasksResumeSuspendedTasks(),
        testId: 'FlowNotificationResumeTasks-' + notification.id,
        closePanel: false,
        onClick: () => {
          this.markAsRead(notification);
          this.resumeTask(content);
        }
      });
      // Tasks
      defaultItems.push({
        title: Messages.tasks(),
        testId: 'FlowNotificationTasks-' + notification.id,
        closePanel: true,
        onClick: () => {
          this.markAsRead(notification);
          Location.instance.setUrl(siteUrls.tasksFlowsUrl());
        }
      });
    } else {
      // Rerun Output Step
      defaultItems.push({
        title: Messages.errorFlowAlertRerun(),
        testId: 'FlowNotificationTasks-' + notification.id,
        closePanel: true,
        onClick: () => {
          this.markAsRead(notification);
          this.rerunOutputSteps(content);
        }
      });
    }
    // Download Flow
    defaultItems.push({
      title: Messages.errorFlowAlertDownload(),
      testId: 'FlowNotificationDownload-' + notification.id,
      closePanel: false,
      onClick: () => {
        this.markAsRead(notification);
        DownloadActions.downloadFlow({ id: content.flowId, name: content.flowName });
      }
    });
    const errors = content.errors;
    const errorCode = errors ? errors[errors.length - 1].errorCode : null;
    // If we have help content for the error code, add menu item to visit the help
    if (errorCode in errorCodesWithHelpContent) {
      defaultItems.push({
        title: Messages.learnMore(),
        testId: 'FlowNotificationError-' + notification.id,
        closePanel: true,
        onClick: () => {
          this.markAsRead(notification);
          Location.instance.setUrl(buildHelpUrl(errorCodesWithHelpContent[errorCode]));
        }
      });
    }
    return defaultItems;
  }

  public getErrorMessage(notification: IUserNotification<FlowNotificationContent>): string {
    /* Check for url error - may be expanded in the future to include other error types */
    const urlError = isNullOrUndefined(notification.targetInfo?.targetUrl);
    return urlError ? Messages.userNotificationFlowNotFoundMessage() : '';
  }

  private markAsRead(notification: IUserNotification<FlowNotificationContent>) {
    if (!notification.read) {
      this.userNotificationActions.updateUserNotificationsStatus([notification.id], true);
    }
  }

  private resumeTask(content: FlowNotificationContent) {
    const runFlowTasks = RunFlowTasks.all().addFilter(ServerFilterClauses.serverFilterClauses.flow(content.flowId));
    SliceableHelper.fetchAll(runFlowTasks).then(tasks => {
      const suspendedTasks = tasks.items.filter((task) => {
        return task.suspended;
      });
      showResumeRunFlowTasksActionDialog(Set.fromIdItemArray(suspendedTasks));
    });
  }

  private rerunOutputSteps(content: FlowNotificationContent) {
    const outputNames = content.errors.map(error => error.errorOutputStepName);
    Flows.getOutputStepInfos(content.flowId).then((outputSteps: ReadonlyArray<FlowOutputStepInfo>) => {
      const outputStepIds = outputNames.map((name) => outputSteps.find(step => step.name === name).id);
      RunFlowOnOutputStepsAction.dialog({
        id: content.flowId,
        name: content.flowName,
        contentType: 'flow'
      },
        outputStepIds,
        this.isIncremental(outputSteps)
      );
    });
  }

  private isIncremental(outputSteps: ReadonlyArray<FlowOutputStepInfo>) {
    return outputSteps.reduce((isIncremental, step) => step.isIncremental || isIncremental, false);
  }
}
